/** A type's scenarios counted by kind, summed and put into words the same way on every surface. */

import type { ScenarioCounts } from '@/services/ai-function.service';
import { counted } from '../format';

/** The most fabricated scenarios one type holds, matching the backend's cap. */
export const FABRICATED_PER_TYPE_MAX = 20;

const NO_SCENARIOS: ScenarioCounts = { ticket: 0, fabricated: 0 };

/** One type's counts from the server's map, where a type with no scenarios has no key. */
export const countsFor = (counts: Record<string, ScenarioCounts>, key: string | null): ScenarioCounts =>
	(key !== null && counts[key]) || NO_SCENARIOS;

export const totalOf = ({ ticket, fabricated }: ScenarioCounts): number => ticket + fabricated;

/** Every group's counts added up, the untyped one's included. */
export const sumCounts = (counts: Record<string, ScenarioCounts>): ScenarioCounts =>
	Object.values(counts).reduce(
		(sum, { ticket, fabricated }) => ({ ticket: sum.ticket + ticket, fabricated: sum.fabricated + fabricated }),
		NO_SCENARIOS
	);

/** "12 past tickets · 3 fabricated", past tickets first; `compact` leaves a zero fabricated count out. */
export const countsLine = (counts: ScenarioCounts, compact = false): string =>
	compact && counts.fabricated === 0
		? counted(counts.ticket, 'past ticket')
		: `${counted(counts.ticket, 'past ticket')} · ${counts.fabricated} fabricated`;

/**
 * What deleting scenarios does to each kind, as a sentence: a past ticket can be mapped again, a
 * fabricated scenario cannot. A kind with none is left out.
 */
export const deletionOutcome = ({ ticket, fabricated }: ScenarioCounts): string => {
	const parts = [
		ticket > 0 && `${counted(ticket, 'past ticket')} ${ticket === 1 ? 'returns' : 'return'} to the pool`,
		fabricated > 0 && `${counted(fabricated, 'fabricated scenario')} ${fabricated === 1 ? 'is' : 'are'} gone for good`
	].filter((part): part is string => Boolean(part));
	return parts.length ? `${parts.join(', and ')}.` : 'There are no scenarios to remove.';
};

/** How many more the type takes before the cap. */
export const fabricationRoom = ({ fabricated }: ScenarioCounts): number =>
	Math.max(0, FABRICATED_PER_TYPE_MAX - fabricated);
